import ng from 'angular';
import { TotalPriceObject } from '@/atomic-components/molecules/forms/form-elements/product-price/product-price';
import { MoleculeFormEditController } from '@/atomic-components/molecules/forms/form-edit/form-edit';
import { UiRights } from '@/configuration';
import {
    AsteriskNoteService,
    AsteriskNoteType,
    AuthContextService,
    BillingHelperService,
    BundleHelperService,
    BundleModelService,
    FunctionRelayService,
    PriceHelperService,
} from '@/services';
import { AccountApi } from '@/types/account-api';
import { BundleApi } from '@/types/bundle-api';

import './bundle-extensions.scss';

const PROJECTION_DEBOUNCE_MS = 300;

interface ExtensionChange {
    productCode: string;
    kind: 'added' | 'removed' | 'changed';
    from: number;
    to: number;
}

interface IncludedProduct {
    capacity: number;
    productCodes: string[];
}

interface CapacityIssue {
    productCode: string;
    capacity: number;
}

interface ErrorSection {
    intro: string;
    showCapacity: boolean;
    items: CapacityIssue[];
}

export class OrganEditPanelBundleExtensionsController {
    public static $inject: string[] = [
        '$scope',
        '$timeout',
        '$translate',
        'asteriskNote',
        'billingHelper',
        'bundleHelper',
        'bundleModel',
        'functionRelay',
        'priceHelper',
    ];

    // Bindings
    public bundle: BundleApi.BundleDetails;
    public bundleAccount: AccountApi.Account | AccountApi.Subaccount;
    public allowedContingentExtensions: BundleApi.BundleContingentExtension[] = [];
    public extensionsChangeable: boolean;

    // Required controllers
    public $editForm: MoleculeFormEditController;

    // Table + edit state
    public showableExtensions: BundleApi.BundlePurchasedContingentExtension[] = [];
    public newExtensions: BundleApi.BundlePurchasedContingentExtension[] = [];
    public availableToAdd: BundleApi.BundleContingentExtension[] = [];
    public extensionsMarkedForDeletion: string[] = [];
    public editMode = false;
    public hasEditRights = false;
    public addDialogOpened = false;

    // Derived display state
    public includedProducts: IncludedProduct[] = [];
    public errorSections: ErrorSection[] = [];
    public openedInfoPanel = '';
    public totalPriceObjects: TotalPriceObject[] = [];
    public productNames: Record<string, string> = {};
    public changesList: ExtensionChange[] = [];
    public additionalCost = 0;
    public additionalCostText = '';
    public costsAccepted = false;
    public extensionInfoTexts: Record<string, string> = {};

    // Asterisk marker types (referenced from template)
    public annualBillingAsteriskNoteType = AsteriskNoteType.ANNUALLY_CONTRACT_PERIOD;

    // Internal
    private contingentUsage: BundleApi.BundleContingentUsage[] = [];
    private originalQuantities: Map<string, number> = new Map();
    private projectionRefreshTimeout: ng.IPromise<void>;

    constructor(
        private $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        private $translate: ng.translate.ITranslateService,
        private asteriskNote: AsteriskNoteService,
        private billingHelper: BillingHelperService,
        private bundleHelper: BundleHelperService,
        private bundleModel: BundleModelService,
        private functionRelay: FunctionRelayService,
        private priceHelper: PriceHelperService,
    ) {}

    // --- Lifecycle ---------------------------------------------------------

    public $onInit(): void {
        this.showableExtensions = this.bundle.purchasedContingentExtensions;
        this.hasEditRights = AuthContextService.isGranted(UiRights.BIL_BUNDLE_EDIT)
            && this.extensionsChangeable !== false;
        this.contingentUsage = this.bundle.effectiveContingentUsage ?? [];
        this.computeAvailableToAdd();
        this.buildContingentUsage();
        this.buildExtensionInfoTexts();
        this.$editForm.saveCallback = this.save;
        this.fetchProductNames();

        this.$scope.$watchCollection(
            () => this.newExtensions,
            () => {
                this.showableExtensions = [
                    ...(this.bundle.purchasedContingentExtensions ?? []),
                    ...(this.newExtensions ?? []),
                ];
                this.computeAvailableToAdd();
                this.computeChangesSummary();
                this.scheduleProjectionRefresh();
            },
        );

        this.$scope.$watchCollection(
            () => this.totalPriceObjects,
            () => this.computeChangesSummary(),
        );
    }

    public $onDestroy(): void {
        this.cancel();
    }

    // --- Public getters (for template) -------------------------------------

    public get vatAsteriskNoteType(): AsteriskNoteType {
        const account = this.bundleAccount ?? AuthContextService.account;
        return account?.isCommercialCustomer
            ? AsteriskNoteType.VAT_EXCLUDED
            : AsteriskNoteType.VAT_INCLUDED;
    }

    public get showAnnualBillingAsterisk(): boolean {
        return this.asteriskNote.preExistingNote(AsteriskNoteType.ANNUALLY_CONTRACT_PERIOD);
    }

    public get disableSaveButton(): boolean {
        return this.$editForm.$invalid
            || (this.additionalCost > 0 && !this.costsAccepted)
            || this.hasErrors;
    }

    public get hasErrors(): boolean {
        return this.errorSections.length > 0;
    }

    public get totalPriceText(): string {
        const priceByCode = this.getUnitPriceMap();
        // Über aktuelle showableExtensions summieren, nicht über die (wachsende) totalPriceObjects-Historie
        // — sonst würden entfernte newExtensions weiter mitgerechnet.
        const active = this.showableExtensions.filter(
            (e) => !this.extensionsMarkedForDeletion.includes(e.productCode),
        );
        if (active.length === 0) {
            return '';
        }
        let sum = 0;
        let currency = '';
        for (const ext of active) {
            const priceObj = priceByCode.get(ext.productCode);
            if (!priceObj) {
                continue;
            }
            sum += (priceObj.rawPrice ?? 0) * (ext.quantity ?? 1);
            if (!currency && priceObj.currency) {
                currency = priceObj.currency.toUpperCase();
            }
        }
        return this.priceHelper.convertPriceToText(1, sum, currency, true, this.bundleAccount);
    }

    // --- Actions (UI-triggered) --------------------------------------------

    public goToEditMode = (): void => {
        this.originalQuantities.clear();
        (this.bundle.purchasedContingentExtensions ?? []).forEach((e) => {
            this.originalQuantities.set(e.productCode, e.quantity);
        });
        this.costsAccepted = false;
        this.editMode = true;
        this.computeChangesSummary();
    };

    public cancel = (): void => {
        this.editMode = false;
        this.extensionsMarkedForDeletion.length = 0;
        (this.bundle.purchasedContingentExtensions ?? []).forEach((e) => {
            const orig = this.originalQuantities.get(e.productCode);
            if (orig !== undefined) {
                e.quantity = orig;
            }
        });
        this.newExtensions.length = 0;
        this.costsAccepted = false;
        if (this.projectionRefreshTimeout) {
            this.$timeout.cancel(this.projectionRefreshTimeout);
            this.projectionRefreshTimeout = undefined;
        }
        this.contingentUsage = this.bundle.effectiveContingentUsage ?? [];
        this.buildContingentUsage();
        this.computeChangesSummary();
    };

    public openAddDialog = (): void => {
        this.addDialogOpened = true;
    };

    public incrementQuantity = (extension: BundleApi.BundlePurchasedContingentExtension): void => {
        extension.quantity = (extension.quantity ?? 0) + 1;
        this.computeChangesSummary();
        this.scheduleProjectionRefresh();
    };

    public decrementQuantity = (extension: BundleApi.BundlePurchasedContingentExtension): void => {
        if ((extension.quantity ?? 0) > 1) {
            extension.quantity = extension.quantity - 1;
            this.computeChangesSummary();
            this.scheduleProjectionRefresh();
        }
    };

    public toggleDelete = (extension: BundleApi.BundlePurchasedContingentExtension): void => {
        if (this.isNewExtension(extension)) {
            const idx = this.newExtensions.findIndex((e) => e.productCode === extension.productCode);
            if (idx >= 0) {
                this.newExtensions.splice(idx, 1);
            }
            this.computeChangesSummary();
            return;
        }
        const idx = this.extensionsMarkedForDeletion.indexOf(extension.productCode);
        if (idx >= 0) {
            this.extensionsMarkedForDeletion.splice(idx, 1);
        } else {
            this.extensionsMarkedForDeletion.push(extension.productCode);
            // Anzahl bei Löschmarkierung auf Ausgangswert zurücksetzen —
            // die Extension wird komplett entfernt, eine geänderte Menge wäre irreführend.
            const originalQty = this.originalQuantities.get(extension.productCode);
            if (originalQty !== undefined) {
                extension.quantity = originalQty;
            }
        }
        this.computeChangesSummary();
        this.scheduleProjectionRefresh();
    };

    public save = (): PromiseLike<unknown> => {
        const bundleToUpdate = ng.copy(this.bundle);
        bundleToUpdate.purchasedContingentExtensions = this.buildPendingExtensions();
        return this.bundleModel.update(bundleToUpdate).then((result) => {
            this.functionRelay.call('updateDeposit', {});
            return result;
        });
    };

    public saveButtonClick = (): void => {
        this.$editForm.save();
    };

    // --- Queries (used from template) --------------------------------------

    public isMarkedForDeletion = (extension: BundleApi.BundlePurchasedContingentExtension): boolean => {
        return this.extensionsMarkedForDeletion.includes(extension.productCode);
    };

    public isNewExtension = (extension: BundleApi.BundlePurchasedContingentExtension): boolean => {
        return this.newExtensions.some((e) => e.productCode === extension.productCode);
    };

    public toggleExtensionInfo = (productCode: string): void => {
        this.openedInfoPanel = this.openedInfoPanel === productCode ? '' : productCode;
    };

    // --- Private helpers ---------------------------------------------------

    private fetchProductNames = (): void => {
        const productCodes = new Set<string>();
        (this.bundle.purchasedContingentExtensions ?? []).forEach(
            (e) => e.productCode && productCodes.add(e.productCode),
        );
        (this.allowedContingentExtensions ?? []).forEach((e) => {
            if (e.productCode) {
                productCodes.add(e.productCode);
            }
            // Auch die enthaltenen Contingent-productCodes holen, für den Info-Tooltip
            (e.contingents ?? []).forEach((c) => {
                (c.productCodes ?? []).forEach((pc) => productCodes.add(pc));
            });
        });
        if (productCodes.size === 0) {
            return;
        }
        void this.billingHelper
            .getPricesByProductCodes(Array.from(productCodes), this.bundle.accountId)
            .then((prices) => {
                const names: Record<string, string> = { ...this.productNames };
                (prices ?? []).forEach((price) => {
                    if (price?.productCode && price?.name) {
                        names[price.productCode] = price.name;
                    }
                });
                this.productNames = names;
                this.buildExtensionInfoTexts();
            });
    };

    private computeAvailableToAdd = (): void => {
        const inTable = new Set(
            (this.showableExtensions ?? []).map((e) => e.productCode),
        );
        this.availableToAdd = (this.allowedContingentExtensions ?? []).filter(
            (e) => e.productCode && !inTable.has(e.productCode),
        );
    };

    private buildContingentUsage = (): void => {
        const included: IncludedProduct[] = [];
        const errorCapacityByCode = new Map<string, number>();
        const mismatchContingentCodes = new Set<string>();
        (this.contingentUsage ?? []).forEach((usage) => {
            const total = usage.totalCapacity ?? 0;
            const used = usage.usedCapacity ?? 0;
            if (total < 0) {
                (usage.productCodes ?? []).forEach((pc) => mismatchContingentCodes.add(pc));
            } else if (total === 0 && used > 0) {
                (usage.productCodes ?? []).forEach((pc) => {
                    errorCapacityByCode.set(pc, (errorCapacityByCode.get(pc) ?? 0) + used);
                });
            }
            if (total <= 0) {
                return;
            }
            included.push({
                capacity: total,
                productCodes: usage.productCodes,
            });
        });
        this.includedProducts = included;
        const errorItems = this.toCapacityIssues(errorCapacityByCode);
        const mismatchItems = this.findExtensionsTouchingContingents(mismatchContingentCodes);
        const sections: ErrorSection[] = [];
        if (errorItems.length) {
            sections.push({
                intro: this.$translate.instant('TR_160726-c6df88_TR'),
                showCapacity: true,
                items: errorItems,
            });
        }
        if (mismatchItems.length) {
            sections.push({
                intro: this.$translate.instant('TR_160726-46c436_TR'),
                showCapacity: false,
                items: mismatchItems,
            });
        }
        this.errorSections = sections;
    };

    private findExtensionsTouchingContingents = (
        contingentCodes: Set<string>,
    ): CapacityIssue[] => {
        if (contingentCodes.size === 0) {
            return [];
        }
        const touching = new Set<string>();
        for (const ext of this.showableExtensions) {
            const allowed = (this.allowedContingentExtensions ?? []).find(
                (a) => a.productCode === ext.productCode,
            );
            const touches = (allowed?.contingents ?? []).some((c) =>
                (c.productCodes ?? []).some((pc) => contingentCodes.has(pc)),
            );
            if (touches && ext.productCode) {
                touching.add(ext.productCode);
            }
        }
        return Array.from(touching).map((productCode) => ({ productCode, capacity: 0 }));
    };

    private toCapacityIssues = (byCode: Map<string, number>): CapacityIssue[] => {
        return Array.from(byCode.entries()).map(
            ([productCode, capacity]) => ({ productCode, capacity }),
        );
    };

    private buildExtensionInfoTexts = (): void => {
        const texts: Record<string, string> = {};
        (this.allowedContingentExtensions ?? []).forEach((ext) => {
            if (!ext.productCode) {
                return;
            }
            const text = this.bundleHelper.formatContingentExtensionInfo(
                ext.productCode,
                this.allowedContingentExtensions,
                this.productNames,
            );
            if (text) {
                texts[ext.productCode] = text;
            }
        });
        this.extensionInfoTexts = texts;
    };

    private scheduleProjectionRefresh = (): void => {
        if (this.projectionRefreshTimeout) {
            this.$timeout.cancel(this.projectionRefreshTimeout);
        }
        this.projectionRefreshTimeout = this.$timeout(() => {
            this.projectionRefreshTimeout = undefined;
            void this.bundleModel
                .bundleUpgradeInfo(this.bundle.id, this.buildPendingExtensions())
                .then((info) => {
                    this.contingentUsage = info?.projectedEffectiveContingentUsage
                        ?? this.bundle.effectiveContingentUsage
                        ?? [];
                    this.buildContingentUsage();
                });
        }, PROJECTION_DEBOUNCE_MS);
    };

    private buildPendingExtensions = (): BundleApi.BundlePurchasedContingentExtension[] => {
        return this.showableExtensions
            .filter((e) => !this.isMarkedForDeletion(e))
            .map((e) => ({ productCode: e.productCode, quantity: e.quantity ?? 1 }));
    };

    private computeChangesSummary = (): void => {
        this.changesList = this.buildChangesList();
        this.additionalCost = this.computeAdditionalCost(this.changesList);
        this.additionalCostText = this.formatAdditionalCost(this.additionalCost);
    };

    private buildChangesList = (): ExtensionChange[] => {
        const changes: ExtensionChange[] = [];
        const markedForDeletion = new Set(this.extensionsMarkedForDeletion);

        for (const ext of this.showableExtensions) {
            const { productCode } = ext;
            const to = ext.quantity ?? 0;
            const from = this.originalQuantities.get(productCode);

            if (from === undefined) {
                changes.push({ productCode, kind: 'added', from: 0, to });
            } else if (markedForDeletion.has(productCode)) {
                changes.push({ productCode, kind: 'removed', from, to: 0 });
            } else if (to !== from) {
                changes.push({ productCode, kind: 'changed', from, to });
            }
        }

        return changes;
    };

    /**
     * Summiert nur kostenpflichtige Deltas (Hinzubuchungen / Mengenerhöhungen).
     * Reduktionen zählen nicht als Ersparnis.
     */
    private computeAdditionalCost = (changes: ExtensionChange[]): number => {
        const priceByCode = this.getUnitPriceMap();
        return changes.reduce((acc, c) => {
            const rawPrice = priceByCode.get(c.productCode)?.rawPrice ?? 0;
            return acc + rawPrice * Math.max(0, c.to - c.from);
        }, 0);
    };

    private formatAdditionalCost = (cost: number): string => {
        if (cost === 0) {
            return '';
        }
        const currency = this.totalPriceObjects[0]?.currency?.toUpperCase() ?? '';
        return this.priceHelper.convertPriceToText(
            1,
            Math.abs(cost),
            currency,
            true,
            this.bundleAccount,
        );
    };

    /**
     * Dedup von totalPriceObjects nach productCode. Bevorzugt Einträge mit rawPrice > 0,
     * damit transiente Loading-Placeholder (rawPrice 0) einen bereits gefetchten Preis
     * nicht überschreiben.
     */
    private getUnitPriceMap = (): Map<string, TotalPriceObject> => {
        const priceByCode = new Map<string, TotalPriceObject>();
        for (const obj of this.totalPriceObjects) {
            if ((obj.rawPrice ?? 0) > 0 || !priceByCode.has(obj.index)) {
                priceByCode.set(obj.index, obj);
            }
        }
        return priceByCode;
    };
}

export class OrganEditPanelBundleExtensionsComponent implements ng.IComponentOptions {
    public bindings = {
        bundle: '<',
        bundleAccount: '<',
        allowedContingentExtensions: '<',
        extensionsChangeable: '<',
    };
    public require = {
        $editForm: '^moleculeFormEdit',
    };
    public template = require('./bundle-extensions.html');
    public controller = OrganEditPanelBundleExtensionsController;
}
